/* ============================================================
   hearo — PDP Scroll Effects
   --------------------------------------------------------------
   Vanilla JS, zero dependencies. Provides:
     1. Scroll-reveal fade+translateY on [data-hearo-reveal]
     2. Stagger-children on [data-hearo-stagger] > *
     3. Auto-attach reveal to all .shopify-section siblings after
        the .hearo-pdp hero (so existing homepage sections animate
        in PDP context without editing them individually)
     4. Sticky ATC-bar observer (shows [data-hearo-sticky-atc]
        when the hero .hearo-pdp__atc scrolls out of the viewport)

   Doctrine compliance (00-CUSTOMER-JOURNEY-DOCTRINE.md §8):
   - No parallax, no auto-play, no hover-only information.
   - prefers-reduced-motion disables all animations (elements
     appear instantly in their final state).
   - IntersectionObserver only — no scroll listeners.
   ============================================================ */

(function () {
  'use strict';

  // Guard against duplicate init if section is re-rendered
  if (window.__hearoPdpEffectsInit) return;
  window.__hearoPdpEffectsInit = true;

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* --- Utility: mark everything revealed (fallback / reduce) --- */
  function revealAll() {
    var nodes = document.querySelectorAll('[data-hearo-reveal]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      el.classList.add('is-in');
      if (el.hasAttribute('data-hearo-stagger')) {
        var kids = el.children;
        for (var k = 0; k < kids.length; k++) {
          kids[k].classList.add('is-in');
        }
      }
    }
  }

  /* --- Auto-attach reveal attribute to sibling sections --- */
  /* The .hearo-pdp hero is inside a .shopify-section wrapper.
     Every following .shopify-section is a homepage/PDP section
     the user wants to see animate as they scroll. We attach the
     reveal attribute to their wrapper so the observer picks them
     up — this avoids editing each section file individually. */
  function autoAttachReveal() {
    var heroInternal = document.querySelector('.hearo-pdp');
    if (!heroInternal) return;
    var heroWrapper = heroInternal.closest('.shopify-section');
    if (!heroWrapper) return;

    var next = heroWrapper.nextElementSibling;
    while (next) {
      if (
        next.classList &&
        next.classList.contains('shopify-section') &&
        !next.querySelector('.hearo-sticky-atc') // never animate the sticky-atc wrapper
      ) {
        if (!next.hasAttribute('data-hearo-reveal')) {
          next.setAttribute('data-hearo-reveal', '');
        }
      }
      next = next.nextElementSibling;
    }
  }

  /* --- Reveal observer --- */
  function initReveals() {
    autoAttachReveal();

    if (reduceMotion || !hasIO) {
      revealAll();
      return;
    }

    var revealObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (!entry.isIntersecting) continue;
          var el = entry.target;
          el.classList.add('is-in');
          revealObserver.unobserve(el);

          if (el.hasAttribute('data-hearo-stagger')) {
            var kids = el.children;
            for (var k = 0; k < kids.length; k++) {
              (function (child, index) {
                child.style.transitionDelay = index * 80 + 'ms';
                window.requestAnimationFrame(function () {
                  child.classList.add('is-in');
                });
              })(kids[k], k);
            }
          }
        }
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -48px 0px'
      }
    );

    var nodes = document.querySelectorAll('[data-hearo-reveal]');
    for (var i = 0; i < nodes.length; i++) {
      revealObserver.observe(nodes[i]);
    }
  }

  /* --- Sticky ATC observer --- */
  /* Watches .hearo-pdp__atc (the hero add-to-cart button). When
     it leaves the viewport (user scrolled past it), the sticky
     bar [data-hearo-sticky-atc] slides up into view. */
  function initStickyATC() {
    var stickyBar = document.querySelector('[data-hearo-sticky-atc]');
    var heroATC = document.querySelector('.hearo-pdp__atc');
    if (!stickyBar || !heroATC) return;

    if (!hasIO) {
      // Fallback: always visible on very old browsers
      stickyBar.classList.add('is-visible');
      return;
    }

    var atcObserver = new IntersectionObserver(
      function (entries) {
        for (var i = 0; i < entries.length; i++) {
          var entry = entries[i];
          if (entry.isIntersecting) {
            stickyBar.classList.remove('is-visible');
          } else {
            stickyBar.classList.add('is-visible');
          }
        }
      },
      {
        threshold: 0,
        rootMargin: '-96px 0px 0px 0px'
      }
    );

    atcObserver.observe(heroATC);
  }

  /* --- Rating → scroll to reviews section -------------------- */
  /* The rating block in the info-card is an <a href="#...">. Native
     anchor navigation jumps instantly; we intercept to provide a
     smooth scroll. Falls back gracefully if the reviews section
     is missing. */
  function initRatingScroll() {
    var ratingLinks = document.querySelectorAll('[data-scroll-to-reviews]');
    if (ratingLinks.length === 0) return;

    function findReviewsTarget() {
      return (
        document.getElementById('shopify-section-hearo_reviews') ||
        document.querySelector('.section-hearo-reviews') ||
        document.querySelector('.hearo-reviews')
      );
    }

    ratingLinks.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var target = findReviewsTarget();
        if (!target) return; // let native anchor behaviour kick in
        e.preventDefault();
        var behavior = reduceMotion ? 'auto' : 'smooth';
        target.scrollIntoView({ behavior: behavior, block: 'start' });
        // Move focus to the section for screen-reader continuity
        if (!target.hasAttribute('tabindex')) {
          target.setAttribute('tabindex', '-1');
        }
        target.focus({ preventScroll: true });
      });
    });
  }

  /* --- Discount modal ---------------------------------------- */
  /* Apply button opens a native <dialog> with an email capture
     form. On submit we POST to Shopify's customer endpoint
     (/contact, form_type=customer, fire-and-forget) then redirect
     to /discount/{code} which applies the code to the cart. The
     new-customer-only rule is enforced on the Shopify discount
     itself (customer eligibility setting), not in this JS. */
  function initDiscountModal() {
    var applyBtns = document.querySelectorAll('[data-discount-apply]');
    if (applyBtns.length === 0) return;

    applyBtns.forEach(function (applyBtn) {
      var modalId = applyBtn.getAttribute('aria-controls');
      var modal = modalId ? document.getElementById(modalId) : null;
      if (!modal) return;

      var closeBtn = modal.querySelector('[data-modal-close]');
      var form = modal.querySelector('form');
      var emailInput = modal.querySelector('[data-modal-email]');
      var submitBtn = modal.querySelector('.hearo-pdp__modal-submit');
      var code = applyBtn.getAttribute('data-discount-code') || '';

      function openModal() {
        if (typeof modal.showModal === 'function') {
          modal.showModal();
        } else {
          // Fallback for browsers without <dialog> support
          modal.setAttribute('open', '');
          modal.style.position = 'fixed';
          modal.style.top = '50%';
          modal.style.left = '50%';
          modal.style.transform = 'translate(-50%, -50%)';
          modal.style.zIndex = '100';
        }
        // Focus email input after paint so the close button isn't focused first
        window.requestAnimationFrame(function () {
          if (emailInput) emailInput.focus();
        });
      }

      function closeModal() {
        if (typeof modal.close === 'function') {
          modal.close();
        } else {
          modal.removeAttribute('open');
          modal.style.display = 'none';
        }
      }

      applyBtn.addEventListener('click', function (e) {
        e.preventDefault();
        openModal();
      });

      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          closeModal();
        });
      }

      // Close on backdrop click (target is the dialog itself, not its children)
      modal.addEventListener('click', function (e) {
        if (e.target === modal) closeModal();
      });

      if (!form) return;

      form.addEventListener('submit', function (e) {
        e.preventDefault();

        var email = emailInput ? emailInput.value.trim() : '';
        if (!email) {
          if (emailInput) emailInput.focus();
          return;
        }

        if (submitBtn) {
          submitBtn.disabled = true;
          submitBtn.classList.add('is-loading');
        }

        var redirectUrl = code ? '/discount/' + encodeURIComponent(code) : '/cart';
        var action = form.getAttribute('action') || '/contact';
        var formData = new FormData(form);

        // Store email locally so analytics / later flows can pick it up
        try { localStorage.setItem('hearo_newsletter_email', email); } catch (_) {}

        // Fire-and-forget POST to Shopify customer endpoint, then redirect
        // regardless of success — the user should always get their discount.
        function redirect() {
          window.location.href = redirectUrl;
        }

        if (typeof fetch === 'function') {
          fetch(action, {
            method: 'POST',
            body: formData,
            credentials: 'same-origin',
            headers: { Accept: 'text/html,application/json' }
          })
            .then(redirect)
            .catch(redirect);

          // Safety net: if the fetch hangs, redirect after 3s anyway
          setTimeout(redirect, 3000);
        } else {
          // Very old browser — just redirect, skip newsletter
          redirect();
        }
      });
    });
  }

  /* ============================================================
     Gallery-Switcher (Phase A1)
     Click/Tap on a thumb → activate matching stage-slide.
     Port aus build/preview-phase-5-4-pdp.html (inline script).
     ============================================================ */
  function initGallerySwitcher() {
    var roots = document.querySelectorAll('.hearo-pdp');
    if (!roots.length) return;

    roots.forEach(function (root) {
      var thumbs = root.querySelectorAll('[data-thumb-index]');
      var slides = root.querySelectorAll('[data-slide-index]');
      if (!thumbs.length || !slides.length) return;

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var idx = thumb.getAttribute('data-thumb-index');

          thumbs.forEach(function (t) {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          slides.forEach(function (s) {
            s.classList.remove('is-active');
            s.setAttribute('aria-hidden', 'true');
          });

          thumb.classList.add('is-active');
          thumb.setAttribute('aria-selected', 'true');

          var slide = root.querySelector('[data-slide-index="' + idx + '"]');
          if (slide) {
            slide.classList.add('is-active');
            slide.setAttribute('aria-hidden', 'false');
          }
        });

        // Keyboard-Support (Enter / Space aktiviert Thumb)
        thumb.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            thumb.click();
          }
        });
      });

      /* --- Touch-Swipe auf der Stage: Mobile-only-Pattern,
             aktiviert next/prev Thumb wie ein nativer Slider --- */
      var stage = root.querySelector('.hearo-pdp__stage');
      if (stage && thumbs.length > 1) {
        var startX = 0;
        var startY = 0;
        var SWIPE_MIN = 40;     // px horizontal nötig für Swipe
        var SWIPE_MAX_V = 60;   // px vertical erlaubt, sonst gilt's als Scroll

        stage.addEventListener('touchstart', function (e) {
          var t = e.changedTouches[0];
          startX = t.clientX;
          startY = t.clientY;
        }, { passive: true });

        stage.addEventListener('touchend', function (e) {
          var t = e.changedTouches[0];
          var dx = t.clientX - startX;
          var dy = t.clientY - startY;
          if (Math.abs(dx) < SWIPE_MIN) return;
          if (Math.abs(dy) > SWIPE_MAX_V) return; // war ein vertical scroll

          var activeThumb = root.querySelector('.hearo-pdp__thumb.is-active');
          if (!activeThumb) activeThumb = thumbs[0];
          var currentIdx = parseInt(activeThumb.getAttribute('data-thumb-index'), 10);
          if (isNaN(currentIdx)) return;

          var nextIdx = dx < 0 ? currentIdx + 1 : currentIdx - 1;
          if (nextIdx >= thumbs.length) nextIdx = 0;
          if (nextIdx < 0) nextIdx = thumbs.length - 1;

          var nextThumb = root.querySelector('[data-thumb-index="' + nextIdx + '"]');
          if (nextThumb) {
            nextThumb.click();
            // Thumb-Leiste mit-scrollen damit aktiver Thumb sichtbar bleibt
            if (typeof nextThumb.scrollIntoView === 'function') {
              nextThumb.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
            }
          }
        }, { passive: true });
      }
    });
  }

  /* ============================================================
     Variant-Picker visual toggle + Bottom-Sheet (Phase A1 + M2b)
     - Inline-Buttons (≤3 Werte): direkte Selection
     - Bottom-Sheet (>3 Werte): Open/Close + Item-Select
     Echter Variant-Swap (Preis/Bild/URL) folgt mit Simple Bundles.
     ============================================================ */
  function initVariantPickerVisual() {
    var roots = document.querySelectorAll('.hearo-pdp');
    if (!roots.length) return;

    roots.forEach(function (root) {
      // Alle Option-Blocks (egal ob inline oder sheet)
      var optionBlocks = root.querySelectorAll('.hearo-pdp__option');

      optionBlocks.forEach(function (block) {
        var allButtons = block.querySelectorAll('.hearo-pdp__option-btn');
        var selectedLabel = block.querySelector('.hearo-pdp__option-selected');
        var triggerValueEl = block.querySelector('[data-option-trigger-value]');
        var triggerSwatch = block.querySelector('.hearo-pdp__option-trigger .hearo-pdp__option-swatch');

        allButtons.forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            var value = btn.getAttribute('data-option-value');

            allButtons.forEach(function (b) {
              b.classList.remove('is-selected');
              b.setAttribute('aria-checked', 'false');
            });
            btn.classList.add('is-selected');
            btn.setAttribute('aria-checked', 'true');

            if (selectedLabel && value) selectedLabel.textContent = value;
            if (triggerValueEl && value) triggerValueEl.textContent = value;

            // Swatch im Trigger updaten: Klassen-Swap
            if (triggerSwatch) {
              // Alte swatch-* Klassen entfernen
              var classes = triggerSwatch.className.split(/\s+/);
              var keep = classes.filter(function (c) { return c.indexOf('swatch-') !== 0; });
              // Neue Klasse aus dem geklickten Item extrahieren
              var itemSwatch = btn.querySelector('.hearo-pdp__option-swatch');
              if (itemSwatch) {
                var itemClasses = itemSwatch.className.split(/\s+/);
                itemClasses.forEach(function (c) {
                  if (c.indexOf('swatch-') === 0) keep.push(c);
                });
              }
              triggerSwatch.className = keep.join(' ');
            }

            // Wenn Button im Sheet liegt: Sheet schliessen
            var sheet = btn.closest('.hearo-pdp__option-sheet');
            if (sheet) closeSheet(sheet);
          });
        });
      });
    });

    // Sheet-Trigger + Close-Buttons
    document.querySelectorAll('[data-option-trigger]').forEach(function (trigger) {
      trigger.addEventListener('click', function () {
        var controls = trigger.getAttribute('aria-controls');
        var sheet = document.getElementById(controls);
        if (sheet) openSheet(sheet, trigger);
      });
    });

    document.querySelectorAll('[data-sheet-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        var sheet = el.closest('.hearo-pdp__option-sheet');
        if (sheet) closeSheet(sheet);
      });
    });

    // ESC zum schliessen
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var openSheets = document.querySelectorAll('.hearo-pdp__option-sheet[aria-hidden="false"]');
      openSheets.forEach(closeSheet);
    });
  }

  function openSheet(sheet, trigger) {
    sheet.setAttribute('aria-hidden', 'false');
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    // Fokus auf Close-Button fuer Keyboard-User
    var closeBtn = sheet.querySelector('.hearo-pdp__option-sheet-close');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 180);
  }

  function closeSheet(sheet) {
    sheet.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    var controls = sheet.id;
    var trigger = document.querySelector('[aria-controls="' + controls + '"]');
    if (trigger) {
      trigger.setAttribute('aria-expanded', 'false');
      trigger.focus();
    }
  }

  /* ============================================================
     Bundle-Selector (Phase A2)
     Click auf ein Bundle-Tile swappt: Preis-Anzeige · ATC-Label ·
     Gallery-Stage-Bild (Bundle-Overlay). 'single' bzw. fehlendes
     Bundle-Image → Restore zum Produkt-Slide 0.
     ============================================================ */
  function initBundleSelector() {
    var selector = document.querySelector('[data-bundle-selector]');
    if (!selector) return;

    var tiles = selector.querySelectorAll('.bb-bundle-tile');
    if (!tiles.length) return;

    var form = document.querySelector('.hearo-pdp__form');
    var variantIdInput = form ? form.querySelector('[data-variant-id-input]') : null;
    var atcLabel = document.querySelector('[data-atc-label]');
    var priceCurrent = document.querySelector('[data-current-price]');
    var priceCompare = document.querySelector('[data-compare-price]');
    var priceSavings = document.querySelector('[data-price-savings]');

    // Bundle-Slide in Gallery (optional, falls gerendert)
    var bundleSlide = document.querySelector('[data-slide-bundle]');
    var bundleStageImg = document.querySelector('[data-bundle-stage-img]');
    var allSlides = document.querySelectorAll('.hearo-pdp__slide');
    var allThumbs = document.querySelectorAll('.hearo-pdp__thumb');

    function deactivateAllSlides() {
      allSlides.forEach(function (s) {
        s.classList.remove('is-active');
        s.setAttribute('aria-hidden', 'true');
      });
    }

    function showBundleSlide(image) {
      if (!bundleSlide || !bundleStageImg || !image) return;
      deactivateAllSlides();
      allThumbs.forEach(function (t) {
        t.classList.remove('is-active');
        t.setAttribute('aria-selected', 'false');
      });
      bundleStageImg.src = image;
      bundleSlide.classList.add('is-active');
      bundleSlide.setAttribute('aria-hidden', 'false');
    }

    function restoreProductSlide() {
      if (bundleSlide) {
        bundleSlide.classList.remove('is-active');
        bundleSlide.setAttribute('aria-hidden', 'true');
      }
      var anyActive = document.querySelector('.hearo-pdp__slide.is-active:not(.hearo-pdp__slide--bundle)');
      if (!anyActive) {
        var firstSlide = document.querySelector('.hearo-pdp__slide[data-slide-index="0"]');
        var firstThumb = document.querySelector('.hearo-pdp__thumb[data-thumb-index="0"]');
        if (firstSlide) { firstSlide.classList.add('is-active'); firstSlide.setAttribute('aria-hidden', 'false'); }
        if (firstThumb) { firstThumb.classList.add('is-active'); firstThumb.setAttribute('aria-selected', 'true'); }
      }
    }

    tiles.forEach(function (tile) {
      tile.addEventListener('click', function () {
        // Toggle selected state
        tiles.forEach(function (t) {
          t.removeAttribute('data-selected');
          t.setAttribute('aria-checked', 'false');
        });
        tile.setAttribute('data-selected', 'true');
        tile.setAttribute('aria-checked', 'true');

        // Pull variant data vom Tile (server-rendered)
        var variantId = tile.getAttribute('data-variant-id');
        var price = tile.getAttribute('data-variant-price');
        var compare = tile.getAttribute('data-variant-compare');
        var savingsAmount = tile.getAttribute('data-variant-savings-amount');
        var savingsPercent = tile.getAttribute('data-variant-savings-percent');
        var label = tile.getAttribute('data-bundle-label');
        var image = tile.getAttribute('data-variant-image');

        // WICHTIG: setze die echte Shopify-Variant-ID im Form
        if (variantIdInput && variantId) {
          variantIdInput.value = variantId;
        }

        // Update ATC-Label
        if (atcLabel && label) atcLabel.textContent = label;

        // Update Preis
        if (priceCurrent && price) priceCurrent.textContent = price;
        if (priceCompare) {
          if (compare) {
            priceCompare.textContent = compare;
            priceCompare.style.display = '';
          } else {
            priceCompare.style.display = 'none';
          }
        }
        if (priceSavings) {
          if (savingsPercent) {
            priceSavings.textContent = 'Spare ' + savingsPercent.replace('−', '');
            priceSavings.style.display = '';
          } else {
            priceSavings.style.display = 'none';
          }
        }

        // Update Gallery-Stage auf Variant-Image (falls vorhanden)
        if (image) {
          showBundleSlide(image);
        } else {
          restoreProductSlide();
        }

        // Feuere change-Event fuer andere Listener (z.B. Shopify Product-Form)
        if (variantIdInput) {
          variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });

    // Thumb-Click beendet Bundle-Mode
    allThumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        if (bundleSlide) {
          bundleSlide.classList.remove('is-active');
          bundleSlide.setAttribute('aria-hidden', 'true');
        }
      });
    });
  }

  /* ============================================================
     Dynamic Delivery Pill (Phase A2)
     Countdown bis naechster Werktag-Cutoff (Default 14:00).
     Lieferfenster: +1 bis +3 Werktage ab Versanddatum,
     Wochenenden werden uebersprungen.
     ============================================================ */
  function initDeliveryPill() {
    var pill = document.querySelector('[data-delivery-pill]');
    if (!pill) return;

    var elCountdown = pill.querySelector('[data-delivery-countdown]');
    var elMain = pill.querySelector('[data-delivery-main]');
    var CUTOFF_HOUR = parseInt(pill.getAttribute('data-cutoff-hour'), 10) || 14;
    var DAY_NAMES = ['So','Mo','Di','Mi','Do','Fr','Sa'];
    var MONTH_NAMES = ['Jan.','Feb.','März','April','Mai','Juni','Juli','Aug.','Sep.','Okt.','Nov.','Dez.'];

    function addBusinessDays(date, days) {
      var d = new Date(date);
      var added = 0;
      while (added < days) {
        d.setDate(d.getDate() + 1);
        var dow = d.getDay();
        if (dow !== 0 && dow !== 6) added++;
      }
      return d;
    }
    function fmtDay(d) { return DAY_NAMES[d.getDay()] + ' ' + d.getDate() + '.'; }
    function fmtDayMonth(d) { return DAY_NAMES[d.getDay()] + ' ' + d.getDate() + '. ' + MONTH_NAMES[d.getMonth()]; }

    function nextCutoff(from) {
      var c = new Date(from);
      c.setHours(CUTOFF_HOUR, 0, 0, 0);
      while (c <= from || c.getDay() === 0 || c.getDay() === 6) {
        c.setDate(c.getDate() + 1);
        c.setHours(CUTOFF_HOUR, 0, 0, 0);
      }
      return c;
    }

    function update() {
      var now = new Date();
      var cutoff = nextCutoff(now);
      var shipsToday = (
        cutoff.getFullYear() === now.getFullYear() &&
        cutoff.getMonth() === now.getMonth() &&
        cutoff.getDate() === now.getDate()
      );

      var shipDate = new Date(cutoff);
      shipDate.setHours(0, 0, 0, 0);
      var delivStart = addBusinessDays(shipDate, 1);
      var delivEnd = addBusinessDays(shipDate, 3);

      var diff = Math.max(0, cutoff - now);
      var h = Math.floor(diff / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      var countdownText = (h >= 1)
        ? 'Bestellt in ' + h + ' h ' + String(m).padStart(2, '0') + ' min'
        : 'Bestellt in ' + m + ' min ' + String(s).padStart(2, '0') + ' s';

      pill.setAttribute('data-ships', shipsToday ? 'today' : 'later');
      if (elCountdown) elCountdown.textContent = countdownText;

      var shipLabel = shipsToday ? 'Versand heute' : 'Versand ' + fmtDay(shipDate);
      var delivLabel = fmtDay(delivStart) + '–' + fmtDayMonth(delivEnd);
      if (elMain) {
        elMain.innerHTML = shipLabel + ' · <strong>Lieferung ' + delivLabel + '</strong>';
      }
    }

    update();
    setInterval(update, 1000);
  }

  /* ============================================================
     Stock-Alert Visibility (Phase A2)
     Server-side-Gate rendert das Element bereits conditional.
     Hier nur MutationObserver fuer Variant-Swap (wenn JS das
     data-inventory-qty Attribut updatet, togglen wir 'hidden').
     ============================================================ */
  function initStockAlert() {
    var el = document.querySelector('[data-stock-alert]');
    if (!el || !('MutationObserver' in window)) return;

    function evaluate() {
      var qty = parseInt(el.getAttribute('data-inventory-qty'), 10);
      var threshold = parseInt(el.getAttribute('data-low-stock-threshold'), 10);
      if (isNaN(threshold) || threshold <= 0) threshold = 10;
      var shouldShow = !isNaN(qty) && qty > 0 && qty <= threshold;
      if (shouldShow) el.removeAttribute('hidden');
      else el.setAttribute('hidden', '');
    }
    var mo = new MutationObserver(evaluate);
    mo.observe(el, { attributes: true, attributeFilter: ['data-inventory-qty', 'data-low-stock-threshold'] });
  }

  /* ============================================================
     Product-Recommendations Custom-Element (Phase A3.1)
     Laedt /recommendations/products.json?section_id=...&product_id=...
     und ersetzt den Section-Inhalt mit dem hydrierten HTML.
     Shopify-Standard-Pattern (aus Dawn adaptiert).
     ============================================================ */
  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        var self = this;
        var url = this.dataset.url;
        if (!url) return;

        // Nur laden wenn noch nicht hydriert (leerer oder skeleton-state)
        var hasRealContent = this.querySelector('.bb-inline-cs__item:not(.bb-inline-cs__item--skeleton)');
        if (hasRealContent) {
          // Shopify hat schon server-side was gerendert, re-init quick-add
          initCrossSellQuickAdd(this);
          return;
        }

        var handleIntersection = function (entries, observer) {
          if (!entries[0].isIntersecting) return;
          observer.unobserve(self);

          fetch(url)
            .then(function (response) { return response.text(); })
            .then(function (text) {
              var html = new DOMParser().parseFromString(text, 'text/html');
              var recommendations = html.querySelector('product-recommendations');
              if (recommendations && recommendations.innerHTML.trim().length) {
                self.innerHTML = recommendations.innerHTML;
                initCrossSellQuickAdd(self);
              } else {
                // Keine Recommendations verfuegbar — Section komplett ausblenden
                var parentSection = self.closest('.bb-cs-section');
                if (parentSection) parentSection.style.display = 'none';
              }
            })
            .catch(function (err) {
              console.warn('[product-recommendations] fetch fehlgeschlagen:', err);
              var parentSection = self.closest('.bb-cs-section');
              if (parentSection) parentSection.style.display = 'none';
            });
        };

        if ('IntersectionObserver' in window) {
          var io = new IntersectionObserver(handleIntersection, { rootMargin: '200px' });
          io.observe(this);
        } else {
          handleIntersection([{ isIntersecting: true }], { unobserve: function () {} });
        }
      }
    });
  }

  /* ============================================================
     Cross-Sell Quick-Add (Phase A3.1)
     Fire-Kreis-Button legt das Produkt per AJAX in den Cart.
     Update des Cart-Icon-Bubble via cart:refresh Event (Dawn-compatibel).
     ============================================================ */
  function initCrossSellQuickAdd(scope) {
    scope = scope || document;
    var buttons = scope.querySelectorAll('[data-cs-add]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      if (btn.__csQuickAddInit) return;
      btn.__csQuickAddInit = true;
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var variantId = btn.getAttribute('data-variant-id');
        var title = btn.getAttribute('data-product-title') || 'Produkt';
        if (!variantId || btn.classList.contains('is-loading')) return;

        btn.classList.add('is-loading');

        var body = JSON.stringify({
          items: [{ id: parseInt(variantId, 10), quantity: 1 }]
        });

        fetch('/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: body
        })
        .then(function (res) {
          if (!res.ok) throw new Error('cart add failed');
          return res.json();
        })
        .then(function () {
          btn.classList.remove('is-loading');
          btn.classList.add('is-added');
          btn.setAttribute('aria-label', title + ' wurde hinzugefuegt');

          // Dawn-Standard Cart-Refresh-Events
          document.dispatchEvent(new CustomEvent('cart:refresh'));
          document.dispatchEvent(new CustomEvent('cart:updated'));

          // Cart-Drawer oeffnen wenn vorhanden
          var cartDrawer = document.querySelector('cart-drawer');
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          } else {
            var cartIcon = document.querySelector('[aria-label*="cart"], #cart-icon-bubble, a[href="/cart"]');
            if (cartIcon) {
              // Fallback: Cart-Count-Bubble aktualisieren per cart.js fetch
              fetch('/cart.js')
                .then(function (r) { return r.json(); })
                .then(function (cart) {
                  var bubble = document.querySelector('.cart-count-bubble, [data-cart-count]');
                  if (bubble) bubble.textContent = cart.item_count;
                });
            }
          }

          // Nach 1.8s: State resetten damit der User nochmal adden kann
          setTimeout(function () {
            btn.classList.remove('is-added');
            btn.setAttribute('aria-label', title + ' zum Warenkorb hinzufuegen');
          }, 1800);
        })
        .catch(function (err) {
          btn.classList.remove('is-loading');
          console.warn('[cross-sell] Cart-Add fehlgeschlagen:', err);
          // Fallback: User auf PDP navigieren
          var item = btn.closest('.bb-inline-cs__item');
          var link = item ? item.querySelector('.bb-inline-cs__name-link') : null;
          if (link && link.href) window.location.href = link.href;
        });
      });
    });
  }

  /* ============================================================
     Details-Accordion: Mobile Bottom-Sheet (Phase A3.2a)
     < 990px: Summary-Click oeffnet Sheet von unten statt inline-Expand
     >= 990px: native <details> behavior (inline accordion)
     ============================================================ */
  function initDetailsSheets() {
    var items = document.querySelectorAll('[data-details-item]');
    if (!items.length) return;

    var MOBILE_BREAKPOINT = 990;

    items.forEach(function (item) {
      var summary = item.querySelector('summary');
      if (!summary) return;

      summary.addEventListener('click', function (e) {
        if (window.innerWidth >= MOBILE_BREAKPOINT) return; // desktop native
        e.preventDefault();

        var sheetId = item.getAttribute('data-sheet-target');
        if (!sheetId) return;
        var sheet = document.getElementById(sheetId);
        if (!sheet) return;

        openDetailsSheet(sheet);

        // details-Element soll NICHT auf Mobile togglen — wir verwalten Sheet selbst
        if (item.hasAttribute('open')) item.removeAttribute('open');
      });
    });

    // Close-Buttons + Backdrop
    document.querySelectorAll('.bb-pdp-details__sheet [data-sheet-close]').forEach(function (el) {
      el.addEventListener('click', function () {
        var sheet = el.closest('.bb-pdp-details__sheet');
        if (sheet) closeDetailsSheet(sheet);
      });
    });

    // ESC schliesst offene Sheets
    document.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var open = document.querySelectorAll('.bb-pdp-details__sheet[aria-hidden="false"]');
      open.forEach(closeDetailsSheet);
    });

    // Bei Viewport-Wechsel Desktop -> Mobile: falls ein details[open] beim
    // Mobile rechnet werden sollte, Sheet zeigen? Nein — zu viel Magic.
    // Wir lassen details-zu bleiben und User muss klicken.
  }

  function openDetailsSheet(sheet) {
    sheet.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    document.body.classList.add('is-details-sheet-open');
    var closeBtn = sheet.querySelector('.bb-pdp-details__sheet-close');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 200);
  }

  function closeDetailsSheet(sheet) {
    sheet.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    document.body.classList.remove('is-details-sheet-open');
  }

  /* ============================================================
     Variant-Swap (Phase B1 — echter Switch)
     ------------------------------------------------------------
     Vorher: initVariantPickerVisual() updated nur Swatch + Label
     visuell, der hidden <input name="id"> blieb auf der Default-
     Variante haengen → User klickt Lila, kauft aber Schwarz, und
     Sold-Out wurde nie erkannt.

     Jetzt: liest das Variant-JSON aus [data-bachgold-variants],
     matcht bei jedem Option-Click die echte Variant, setzt:
       - hidden Input(s) (Hero + Sticky)
       - Preis + Compare-Preis
       - Add-to-Cart-Button: 3 States
           1. available  → Submit-Button "In den Warenkorb"
           2. sold-out + Klaviyo Public Key gesetzt → BIS-Trigger
           3. sold-out ohne Klaviyo → disabled "Ausverkauft"
       - URL-Param ?variant=ID

     Sold-out-Logik: available==false ODER (tracking AN UND stock<=0).
     Damit greift sold-out auch bei inventory_policy='continue'
     (Continue-selling AN), wo Shopify available=true zurueckgibt.
     ============================================================ */
  function initVariantSwap() {
    var root = document.querySelector('.hearo-pdp');
    if (!root) return;

    var variantsScript = root.querySelector('[data-bachgold-variants]');
    var configScript = root.querySelector('[data-bachgold-pdp-config]');
    if (!variantsScript || !configScript) return;

    var variants;
    var config;
    try {
      variants = JSON.parse(variantsScript.textContent);
      config = JSON.parse(configScript.textContent);
    } catch (e) {
      console.warn('[bachgold-variant-swap] JSON parse failed:', e);
      return;
    }
    if (!Array.isArray(variants) || !variants.length || !config) return;

    var optionNames = Array.isArray(config.optionNames) ? config.optionNames : [];

    function formatMoney(cents) {
      if (cents == null) return '';
      return (cents / 100).toFixed(2).replace('.', ',') + ' €';
    }

    function getSelections() {
      var sel = new Array(Math.max(optionNames.length, 1)).fill(null);

      root.querySelectorAll('.hearo-pdp__option').forEach(function (block) {
        var btn = block.querySelector('.hearo-pdp__option-btn.is-selected')
          || block.querySelector('.hearo-pdp__option-btn[aria-checked="true"]')
          || block.querySelector('.hearo-pdp__option-btn');
        if (!btn) return;
        var idx = parseInt(btn.getAttribute('data-option-index'), 10);
        var val = btn.getAttribute('data-option-value');
        if (!isNaN(idx) && val != null) sel[idx] = val;
      });

      root.querySelectorAll('[data-hidden-option]').forEach(function (input) {
        var name = input.getAttribute('name') || '';
        var match = name.match(/options\[(.+?)\]/);
        if (!match) return;
        var idx = optionNames.indexOf(match[1]);
        if (idx >= 0) sel[idx] = input.value;
      });

      return sel;
    }

    function findVariant(selections) {
      return variants.find(function (v) {
        var opts = v.options || [];
        for (var i = 0; i < opts.length; i++) {
          if (selections[i] != null && selections[i] !== opts[i]) return false;
        }
        return true;
      });
    }

    function isVariantSoldOut(v) {
      if (!v) return true;
      if (v.available === false) return true;
      if (v.inventory_management === 'shopify'
          && v.inventory_quantity != null
          && v.inventory_quantity <= 0) return true;
      return false;
    }

    function setLabel(btn, text) {
      var label = btn && btn.querySelector('[data-atc-label], [data-sticky-btn-label]');
      if (label) label.textContent = text;
    }

    function applySoldOutBanner(variant) {
      var banner = document.querySelector('[data-sold-out-banner]');
      if (!banner) return;
      var soldOut = isVariantSoldOut(variant);
      if (soldOut) {
        banner.hidden = false;
        banner.setAttribute('aria-hidden', 'false');
        var nameEl = banner.querySelector('[data-sold-out-variant-name]');
        if (nameEl) {
          nameEl.textContent = variant.title && variant.title !== 'Default Title'
            ? variant.title
            : 'Diese Variante';
        }
      } else {
        banner.hidden = true;
        banner.setAttribute('aria-hidden', 'true');
      }
    }

    function applyAtcButtonState(btn, variant) {
      if (!btn) return;
      var soldOut = isVariantSoldOut(variant);
      var bisActive = soldOut && !!config.publicKey;

      if (bisActive) {
        btn.setAttribute('type', 'button');
        btn.classList.add('bachgold-klaviyo-bis-trigger');
        btn.removeAttribute('disabled');
        btn.removeAttribute('name');
        btn.setAttribute('data-klaviyo-bis', 'true');
        btn.setAttribute('data-product-id', config.productId || '');
        btn.setAttribute('data-product-handle', config.productHandle || '');
        btn.setAttribute('data-product-title', config.productTitle || '');
        btn.setAttribute('data-product-url', config.productUrl || '');
        btn.setAttribute('data-variant-id', variant.id);
        btn.setAttribute('data-variant-title', variant.title || '');
        if (variant.featured_image && variant.featured_image.src) {
          btn.setAttribute('data-product-image', 'https:' + variant.featured_image.src);
        }
        setLabel(btn, config.notifyMeLabel || 'Benachrichtige mich, wenn wieder verfügbar');
      } else if (soldOut) {
        btn.setAttribute('type', 'submit');
        btn.classList.remove('bachgold-klaviyo-bis-trigger');
        btn.setAttribute('disabled', 'disabled');
        btn.setAttribute('name', 'add');
        btn.removeAttribute('data-klaviyo-bis');
        setLabel(btn, config.soldOutLabel || 'Ausverkauft');
      } else {
        btn.setAttribute('type', 'submit');
        btn.classList.remove('bachgold-klaviyo-bis-trigger');
        btn.removeAttribute('disabled');
        btn.setAttribute('name', 'add');
        btn.removeAttribute('data-klaviyo-bis');
        var text = config.ctaLabel || 'In den Warenkorb';
        // Sticky-ATC: Preis steht bereits links — Suffix nur für Hero-ATC
        var isSticky = btn.classList.contains('bb-sticky-atc__btn');
        if (!isSticky && config.showPriceInCta && variant.price != null) {
          text += ' — ' + formatMoney(variant.price);
        }
        setLabel(btn, text);
      }
    }

    function applyPrice(variant) {
      var current = root.querySelector('[data-current-price]');
      if (current && variant.price != null) {
        current.textContent = formatMoney(variant.price);
      }
      var compare = root.querySelector('[data-compare-price]');
      if (compare) {
        if (variant.compare_at_price && variant.compare_at_price > variant.price) {
          compare.textContent = formatMoney(variant.compare_at_price);
          compare.style.display = '';
        } else {
          compare.style.display = 'none';
        }
      }
    }

    var lastAppliedId = null;

    /* Variant-Image-Switch: matcht variant.featured_image.id (Shopify-Image-Resource)
       gegen data-media-image-id der Thumbs. Klick simuliert → existing
       Gallery-Switcher uebernimmt Stage-Slide-Toggle inkl. Active-State.
       Fallback: data-media-id (Media-Object-ID) falls Image-Resource-ID fehlt.
       Silent fail wenn Thumb nicht existiert (z.B. Media-Limit 8 ueberschritten
       oder Variante hat kein eigenes Image). */
    function applyVariantImage(variant) {
      if (!variant) return;
      var img = variant.featured_image;
      var mediaId = variant.featured_media && variant.featured_media.id;
      if (!img && !mediaId) return;

      var thumb = null;
      if (img && img.id) {
        thumb = root.querySelector('.hearo-pdp__thumb[data-media-image-id="' + img.id + '"]');
      }
      if (!thumb && mediaId) {
        thumb = root.querySelector('.hearo-pdp__thumb[data-media-id="' + mediaId + '"]');
      }
      if (!thumb || thumb.classList.contains('is-active')) return;
      thumb.click();
    }

    function applyVariant(variant) {
      if (!variant || variant.id === lastAppliedId) return;
      lastAppliedId = variant.id;

      // Hidden ID inputs: Hero + Sticky-ATC + Bundle-Master
      document.querySelectorAll('[data-variant-id-input], [data-sticky-variant-id]').forEach(function (inp) {
        inp.value = variant.id;
        inp.removeAttribute('disabled');
      });

      applyPrice(variant);

      // Hero + Sticky ATC button states
      applyAtcButtonState(document.querySelector('.hearo-pdp__atc'), variant);
      applyAtcButtonState(document.querySelector('.bb-sticky-atc__btn'), variant);

      // Inline-Banner über ATC
      applySoldOutBanner(variant);

      // Gallery: switch to variant's featured image
      applyVariantImage(variant);

      // URL update
      if (window.history && window.history.replaceState) {
        try {
          var url = new URL(window.location.href);
          url.searchParams.set('variant', String(variant.id));
          window.history.replaceState({}, '', url.toString());
        } catch (e) { /* noop */ }
      }

      // Notify legacy listeners (e.g. pickup-availability, cart-drawer)
      document.querySelectorAll('[data-variant-id-input], [data-sticky-variant-id]').forEach(function (inp) {
        inp.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    // Click on any option button → re-match variant after visual handler ran
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('.hearo-pdp__option-btn');
      if (!btn) return;
      var fromSheet = !!btn.closest('.hearo-pdp__option-sheet');
      var clickedSoldOut = btn.getAttribute('data-option-sold-out') === 'true';

      setTimeout(function () {
        var variant = findVariant(getSelections());
        if (!variant) return;
        applyVariant(variant);

        // Sheet-Click auf sold-out → direkt BIS-Modal öffnen via globale API
        if (fromSheet && clickedSoldOut && isVariantSoldOut(variant) && config.publicKey) {
          setTimeout(function () {
            if (window.BachgoldBis && typeof window.BachgoldBis.open === 'function') {
              window.BachgoldBis.open({
                productId: config.productId,
                productHandle: config.productHandle,
                productTitle: config.productTitle,
                productUrl: config.productUrl,
                productImage: variant.featured_image && variant.featured_image.src
                  ? 'https:' + variant.featured_image.src
                  : '',
                variantId: variant.id,
                variantTitle: variant.title || ''
              });
            }
          }, 320);
        }
      }, 0);
    });

    // External variant-id changes (e.g. Bundle-Tile-Click in initBundleSelector)
    document.querySelectorAll('[data-variant-id-input], [data-sticky-variant-id]').forEach(function (inp) {
      inp.addEventListener('change', function () {
        var newId = parseInt(inp.value, 10);
        if (!newId || newId === lastAppliedId) return;
        var v = variants.find(function (x) { return x.id === newId; });
        if (v) applyVariant(v);
      });
    });

    // Initial sync: apply server-side selected_or_first_available_variant
    var initial = findVariant(getSelections());
    if (initial) applyVariant(initial);
  }

  /* --- Boot --- */
  function init() {
    initReveals();
    initStickyATC();
    initRatingScroll();
    initDiscountModal();
    initGallerySwitcher();
    initVariantPickerVisual();
    initVariantSwap();
    initBundleSelector();
    initDeliveryPill();
    initStockAlert();
    initCrossSellQuickAdd();
    initDetailsSheets();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
